
/**
 * Authentication utility functions for secure token handling
 */

// Store auth token securely with expiration
export const setAuthToken = (token: string, type: 'candidate' | 'employer'): void => {
  // Set expiration to 8 hours from now
  const expiration = new Date();
  expiration.setHours(expiration.getHours() + 8);
  
  const tokenData = {
    token,
    expires: expiration.toISOString(),
    userType: type
  };
  
  // Store in sessionStorage instead of localStorage for better security
  sessionStorage.setItem('authData', JSON.stringify(tokenData));
};

// Get the auth token if valid
export const getAuthToken = (): string | null => {
  const authData = sessionStorage.getItem('authData');
  
  if (!authData) return null;
  
  try {
    const parsed = JSON.parse(authData);
    const expirationDate = new Date(parsed.expires);
    
    // Check if token is expired
    if (expirationDate <= new Date()) {
      clearAuthToken();
      return null;
    }
    
    return parsed.token;
  } catch (error) {
    console.error('Error parsing auth data:', error);
    clearAuthToken();
    return null;
  }
};

// Get user type (candidate or employer)
export const getUserType = (): 'candidate' | 'employer' | null => {
  const authData = sessionStorage.getItem('authData');
  
  if (!authData) return null;
  
  try {
    const parsed = JSON.parse(authData);
    return parsed.userType;
  } catch (error) {
    console.error('Error parsing auth data:', error);
    return null;
  }
};

// Clear auth token on logout
export const clearAuthToken = (): void => {
  sessionStorage.removeItem('authData');
};

// Store employer info securely
export const setEmployerInfo = (email: string, companyName: string): void => {
  const expiration = new Date();
  expiration.setHours(expiration.getHours() + 8);
  
  const employerData = {
    email,
    companyName,
    expires: expiration.toISOString()
  };
  
  sessionStorage.setItem('employerData', JSON.stringify(employerData));
};

// Get employer info if valid
export const getEmployerInfo = (): { email: string, companyName: string } | null => {
  const employerData = sessionStorage.getItem('employerData');
  
  if (!employerData) return null;
  
  try {
    const parsed = JSON.parse(employerData);
    const expirationDate = new Date(parsed.expires);
    
    if (expirationDate <= new Date()) {
      clearEmployerInfo();
      return null;
    }
    
    return {
      email: parsed.email,
      companyName: parsed.companyName
    };
  } catch (error) {
    console.error('Error parsing employer data:', error);
    clearEmployerInfo();
    return null;
  }
};

// Clear employer info
export const clearEmployerInfo = (): void => {
  sessionStorage.removeItem('employerData');
};

// Store candidate info securely
export const setCandidateInfo = (name: string, email: string): void => {
  const candidateData = {
    name,
    email
  };
  
  sessionStorage.setItem('candidateInfo', JSON.stringify(candidateData));
};

// Get candidate info
export const getCandidateInfo = (): { name: string, email: string } | null => {
  const candidateData = sessionStorage.getItem('candidateInfo');
  
  if (!candidateData) return null;
  
  try {
    const parsed = JSON.parse(candidateData);
    return {
      name: parsed.name,
      email: parsed.email
    };
  } catch (error) {
    console.error('Error parsing candidate data:', error);
    return null;
  }
};

// Sanitize input to prevent XSS attacks
export const sanitizeInput = (input: string): string => {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};
